import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useModal } from '@jutro/components';
import { MLFormService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import { LoadSaveService } from 'gw-capability-gateway-quoteandbind';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { UserService } from 'gw-capability-gateway';
import { GatewayDocumentService } from 'gw-capability-gateway-document';
import { MLModalService , MLTagManagerService } from 'ml-portals-services-react';
import { MLUserTypeUtil } from 'ml-portals-util-js';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import { EndorsementService } from 'gw-capability-policychange';
import { AccountService } from 'gw-capability-gateway-policy';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import PolChangeTrackEvent from 'gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/PolChangeTrackEvent.jsx';

import appConfig from 'app-config';
import DisclaimerComponent from "gw-capability-gateway-quoteandbind-common-react/components/DisclaimerComponent/DisclaimerComponent";
import { Identify  } from '../../../gw-capability-gateway-quoteandbind-common-react/components/TrackEvent/TrackEvent';
import VerbalAuthorizationComponent from '../../components/VerbalAuthorizationComponent/VerbalAuthorizationComponent';
import RequiredSignaturesComponent from '../../components/RequiredSignaturesComponent/RequiredSignaturesComponent';
import AvailableDocumentsComponent from '../../components/AvailableDocumentsComponent/AvailableDocumentsComponent';

import ApplySignatureTypeToAllPopupMetadata from './ApplySignatureTypeToAllPopup.metadata.json5';

import metadata from './AgentSignatureOptionsPage.metadata.json5';
import messages from './AgentSignatureOptionsPage.messages';

const {
    MLFormNumberWildcards
} = appConfig;

const steps = {
    VERBAL_AUTH: 'verbal_auth',
    DISCLAIMER: 'disclaimer',
    REQUIRED_SIGNATURES: 'required_signatures',
    AVAILABLE_DOCUMENTS: 'available_documents'
};

const AgentSignatureOptionsPage = (props) => {
    const modalApi = useModal();
    const { wizardData, updateWizardData, goNext ,jumpTo , steps: pageSteps } = props;
    const { submissionVM: endorsementVM } = wizardData;
    const [currentStep, setCurrentStep] = useState(null);
    const [eSignatureForms , setESignatureForms ] = useState([]);
    const [requiredSignaturesForms, setRequiredSignaturesForms] = useState([]);
    const [availableDocumentsForms , setAvailableDocumentsForms ] = useState([]);
    const [displayEmailWarning, setDisplayEmailWarning] = useState(false)
    //DE255327 field level error message's
    const [showValidationErrors, setShowErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);
    const [oecRadioSelection ,setoecRadioSelection] = useState(null);
    const [contingencies, setcontingencies] = useState(endorsementVM?.value?.preBindContingenciesToDTO || []);

    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();
    const { onValidate, isComponentValid } = useValidation('AgentSignatureOptionsPage');
    const { portalCode } = appConfig;  
    const [scriptML, setScriptML] = useState();
    const gafInfo = _.get(endorsementVM, 'baseData.gafInfo_ML.value', {});
    const { mercerOrganization } = appConfig;
    const { mercerGpcCode } = appConfig;

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    //flag for click buttion US993539
    const [isFileUploadDone, setIsFileUploadDone] = useState(false);

    const SIGNATURETYPE_TYPELIST_FULL = viewModelService.productMetadata.get('pc').types.getTypelist('SignatureType_ML');

    const producerCode_ML = endorsementVM.baseData.producerCodeOfService_ML.value;

    const isGpa = true;
    const isAgent = appConfig.portalCode === 'GPA';
    const allForms = endorsementVM.baseData.forms_ML;
    const uwIssues = endorsementVM.uwIssues_ML.value;
    const jobID = _.get(endorsementVM, 'jobID.value');

    const {
        showProgressDialog,
        showErrorModal,
        hideProgressDialog
    } = MLModalService();

    const {
        addWarningAlert,
        clearBlockAlerts,
        clearWarningAlerts
    } = MLNotificationBlockUtil();

    const getPageHeader = () => {
        switch (currentStep) {
            case steps.VERBAL_AUTH: {
                return messages.verbalAuthorization;
            }
            case steps.DISCLAIMER: {
                return messages.disclaimer;
            }
            case steps.REQUIRED_SIGNATURES: {
                return messages.requiredSignatures;
            }
            case steps.AVAILABLE_DOCUMENTS: {
                return messages.availableDocuments;
            }
            default: {
                return undefined;
            }
        }
    };

    useEffect(() => {
        Identify('Endorsement Step Reached', {step: 'Agent Signature Options Screen'}, endorsementVM, authHeader)
    }, [])

    const isDisclaimerScriptAvailable = useCallback(async () => {
        if (isGpa) {
            const scriptRequest = { quoteID: jobID };

            showProgressDialog(platformMessages.loading, null, null, 0);

            const agentScript = await LoadSaveService.getScript('disclaimers', scriptRequest, authHeader);

            hideProgressDialog();

            if (!_.isEmpty(agentScript)) {
                setScriptML(agentScript);

                return true
            }

            return false
        }

        return false
    }, [authHeader, hideProgressDialog, isGpa, jobID, showProgressDialog]);

    const initializeDisclaimerStep = async () => {
        const hasDisclaimers = await isDisclaimerScriptAvailable();

        if (hasDisclaimers) {
            setCurrentStep(steps.DISCLAIMER);

            return;
        }

        setCurrentStep(steps.REQUIRED_SIGNATURES);
    }

    useEffect(() => {
        const initializePage = async () => {
            if((MLUserTypeUtil.isGcsOrMetGaUser(endorsementVM.baseData) && allForms.children.find((form) => {
                return !!form.verbalAuthorization_ML.value;
            }))) {
                setCurrentStep(steps.VERBAL_AUTH);

                return;
            }

            initializeDisclaimerStep();
        }

        initializePage();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    const verbalAuthForms = useMemo(() => {
        return allForms.filter((form) => {
            return form.verbalAuthorization_ML?.value === true;
        });
    }, [allForms]);

    const getESignatureForms = useCallback(() => {
        return allForms.filter((form) => {
            return form.esigned_ML?.value === true;
        });
    }, [allForms]);

    const getUncheckedAndESigFormsValues = useCallback(() => {
        return (allForms || []).filter((form) => {
            return form?.esigned_ML?.value === true && !(form?.verbalAuthorization_ML?.value === true && form?.isSigned?.value === true);
        });
    }, [allForms]);

    const getAllVerbalAndESigForms = () => {
        return [...new Set(verbalAuthForms.concat(eSignatureForms))];
    };

    const signatureTypeList = SIGNATURETYPE_TYPELIST_FULL.codes
        .filter((sigType) => { return sigType.code !== 'verbal' && sigType.code !== 'signature_on_file'; });
    const appSignatureTypeList = SIGNATURETYPE_TYPELIST_FULL.codes
        .filter((sigType) => { return sigType.code !== 'verbal'; });

    const [isUserRetentionAgent, setIsUserRetentionAgent] = useState(_.get(producerCode_ML, 'isDocRetention_ML', false));

    const [currentUser, setCurrentUser] = useState({});

    const [organization, setOrganization] = useState(undefined);

    const getCurrentUser = () => {
        if (isGpa) {
            UserService.getCurrentUser(authHeader).then((user) => {
                setCurrentUser(user);
                const producerCodes = user.producerCodes_ML;
                if (user.userType !== undefined && producerCodes !== undefined) {
                    const producer = producerCodes.find((item) => { return producerCode_ML.code === item.code; });
                    setIsUserRetentionAgent(_.get(producer, 'isDocRetention_ML', false));
                }
                if (user?.primaryProducerCode_ML?.organization_ML) {
                    setOrganization(user.primaryProducerCode_ML.organization_ML);
                }
            });
        }
    };

    useEffect(() => {
        getCurrentUser();
        setESignatureForms(getESignatureForms());
        setRequiredSignaturesForms(getUncheckedAndESigFormsValues());
        setAvailableDocumentsForms(getAllVerbalAndESigForms());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

     useEffect(() => {
         if (isAgent) {
             PolChangeTrackEvent('Endorsement Step Reached', false, endorsementVM, authHeader, { step: 'PolChange Agent Signature Options Screen' });
         }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const convertDateToUTC = (date) => {
        if (date) {
            return date.toLocaleString('en-US', { timeZone: 'UTC', month: 'numeric', day: 'numeric', year: '2-digit', hour: '2-digit', minute: '2-digit' });
        }
        return '';
    };

    const isSignatureOnFileAvailable = (form) => {
        return isGpa && (currentUser.isIA || currentUser.isPCS || currentUser.isMetGA)
            && (form.formNumber.value.startsWith(MLFormNumberWildcards.AUTO_APPLICATION_FORM)
                || form.formNumber.value.startsWith(MLFormNumberWildcards.HOME_APPLICATION_FORM)
                || form.formNumber.value.startsWith(MLFormNumberWildcards.UMBRELLA_APPLICATION_FORM));
    };

    const shouldShowSecondEmail = (form) => {
            return form.secondEmailRequired.value || shouldShowExcludedDriverEmail(form) || shouldShowSNIEmail(form) || !!form?.secondaryInsuredEmailRequired?.value;
        };
    
    const shouldShowExcludedDriverEmail = (form) => {
            if (!_.isUndefined(form?.esignatureOptions?.value?.code) && form?.esignatureOptions?.value?.code === 'Text' && !_.isUndefined(form?.excludedDriverEmailRequired?.value) && form?.excludedDriverEmailRequired?.value) {
                return true;
            }
            return false;
        };

    const shouldShowSNIEmail = (form) => {
                if (!_.isUndefined(form?.esignatureOptions?.value?.code) && form?.esignatureOptions?.value?.code === 'Email' && !_.isUndefined(form?.secondEmailRequired?.value) && !form?.secondEmailRequired?.value && !_.isUndefined(form?.secondaryInsuredEmailRequired?.value) && form?.secondaryInsuredEmailRequired?.value) {
                    return true;
                }
                return false;
            };

    const shouldShowSecondMobile = (form) => {
        return form?.secondaryMobileNumberRequired?.value || shouldShowExcludedMobile(form);
    }

    const shouldShowExcludedMobile = (form) => {
        if (!_.isUndefined(form?.esignatureOptions?.value?.code) && form?.esignatureOptions?.value?.code === 'Text' && !_.isUndefined(form?.excludedDriverMobileNumberRequired?.value) && form?.excludedDriverMobileNumberRequired?.value) {
            return true;
        }
        return false;
    };

    const checkIfEmailsAreDuplicated = (form) => {
        if (shouldShowSecondEmail(form)) {
            if (form.namedInsuredEmail && form.secondEmail) {
                return form.namedInsuredEmail.value === form.secondEmail.value;
            }
        }
        return false;
    };

    const [promptToApplySignature, setPromptToApplySignature] = useState(true);

    const signatureTypeChange = (rsForm) => {
        if (requiredSignaturesForms.length > 1) {
            if (rsForm.signatureType.value && promptToApplySignature) {
                const componentProps = {
                    title: messages.applySignature,
                    uiProps: ApplySignatureTypeToAllPopupMetadata.pageContent,
                    actionBtnLabel: platformMessages.mlYes,
                    cancelBtnLabel: platformMessages.mlNo,
                    onSaveAction: () => {
                        requiredSignaturesForms.forEach((form) => {
                            form.signatureType.value = rsForm.signatureType.value;
                            if (isUserRetentionAgent && rsForm.signatureType.value.code !== 'esignature') {
                                form.isSigned.value = true;
                                form.signedDateTime.value = new Date();
                            }
                            updateWizardData(wizardData);
                        });
                        return true;
                    }
                };
                modalApi.showModal(
                    <FormModalPopover {...componentProps} />
                );
                setPromptToApplySignature(false);
            }

            // when apply signature type to all list select no
            if (isUserRetentionAgent && !rsForm.isSigned.value && rsForm.signatureType.value.code !== 'esignature') {
                rsForm.isSigned.value = true;
                rsForm.signedDateTime.value = new Date();
            }
        }
        refreshData();
    };


    const clearActionFields = (form) => {
        if (!form.signatureType.value) {
            return;
        }
        if (form.signatureType.value.code !== 'fax') {
            _.set(form, 'faxNumber.value', _.get(endorsementVM, 'baseData.primaryNamedInsured_ML.faxNumber.value'));
        }
        if (form.signatureType.value.code !== 'email' && form.signatureType.value.code !== 'esignature') {
            form.namedInsuredEmail.value = form.namedInsuredEmailDefault;
            if (shouldShowSecondEmail(form)) {
                form.secondEmail.value = form.secondEmailDefault;
            }
        }
        if (form.signatureType.value.code !== 'usps' && form.signatureType.value.code !== 'hard_copy' &&
            form.signatureType.value.code !== 'agent_esignature') {
            form.formLanguage.value = null;
        }
    };

    const shouldShowLanguageOption = (form) => {
        return false && form === null;
        // TODO: need new Spanish indicator in Form Pattern to support Language Option
        // return form.signatureType && form.signatureType.value && (form.signatureType.value.code === 'agent_esignature' || form.signatureType.value.code === 'hard_copy') && form.formNumber.value.indexOf('SP-') != -1;
    };

    const checkActionsCompleted = () => {
        return requiredSignaturesForms.every((form) => {
            if (!form.signatureType.value) {
                return false;
            }
            if (form.signatureType.value.code === 'fax' && !form.faxNumber.value) {
                return false;
            }
            if (form.signatureType.value.code === 'email' || form.signatureType.value.code === 'esignature') {
                if (checkIfEmailsAreDuplicated(form)) {
                    return false;
                }
            }
            if (shouldShowLanguageOption(form) && !form.formLanguage.value) {
                return false;
            }
            return true;
        });
    };

    const updateForms = () => {
        setRequiredSignaturesForms(getUncheckedAndESigFormsValues());
        setAvailableDocumentsForms(getAllVerbalAndESigForms());
    };

    const [blockedByUWIssues, setBlockedByUWIssues] = useState(false);

    const blockingUWIssuesPresentCallback = () => {
        endorsementVM.blockNavigation_ML.value = true;
        setBlockedByUWIssues(true);
    };

    const approvedAllBlockingUWIssuesCallback = () => {
        endorsementVM.blockNavigation_ML.value = false;
        setBlockedByUWIssues(false);
    };

    const viewDocument = (item) => {
        MLTagManagerService.sendLinkClickEvent('view_'.concat(item.value.formDescription));
        const sessionID = endorsementVM.sessionUUID.value;
        const foreignId = endorsementVM.jobID.value;
        const documentNotFoundURL = `${window.location.pathname.substring(0, window.location.pathname.lastIndexOf('/'))}/PolicyDocumentError`;

        const appendToUrl = `${foreignId}?form=${item.formNumber.value}&token=${sessionID}&portalRoute=${documentNotFoundURL}`;

        const displayErrorModal = () => {
            hideProgressDialog();
            showErrorModal(platformMessages.error, platformMessages.unableToDownloadDocument);
        };

        const downloadPromise = GatewayDocumentService.downloadDocumentPreview(appendToUrl, authHeader, displayErrorModal, true);
        showProgressDialog(platformMessages.downloadingDocument, downloadPromise);
    };
    const isDisclaimerActionCompleted = () => !_.isNull(oecRadioSelection)

    const isNextButtonDisabled = () => {
        switch (currentStep) {
            case steps.DISCLAIMER:{
                return !isDisclaimerActionCompleted();
            }
            case steps.REQUIRED_SIGNATURES: {
                return !checkActionsCompleted();
            }
            case steps.AVAILABLE_DOCUMENTS: {
                //US993539
                return blockedByUWIssues || isFileUploadDone;
            }
            default: {
                return false;
            }
        }
    };

    const getNextDescription = () => {
        switch (currentStep) {
            case steps.VERBAL_AUTH: {
                return (
                    <FormattedMessage
                        {...messages.verbalAuthorizationNextDescription}
                        values={{
                            requiredForms: <strong>{translator(messages.requiredForms)}</strong>
                        }}
                    />
                );
            }
            case steps.REQUIRED_SIGNATURES: {
                return (
                    <FormattedMessage
                        {...messages.requiredSignatureNextDescription}
                        values={{
                            yourDocuments: <strong>{translator(messages.yourDocuments)}</strong>
                        }}
                    />
                );
            }
            case steps.AVAILABLE_DOCUMENTS: {
                return (
                    <FormattedMessage
                        {...messages.availableDocumentsNextDescription}
                        values={{
                            confirmYourPolicy: <strong>{translator(messages.confirmYourPolicy)}</strong>
                        }}
                    />
                );
            }
            default: {
                return undefined;
            }
        }
    };

    const goToQuotePage = () => {
        const index = _.findIndex(pageSteps, ({ path }) => path === '/summary');
        
        jumpTo(index);
    }

    const getNewlyAddedForms = () => {
        const updateFormPromise = MLFormService.getNewlyAddedForms(endorsementVM.jobID.value, authHeader);

                showProgressDialog(messages.updatingForms, updateFormPromise);

              return  updateFormPromise.then((res) => {

                    endorsementVM.baseData.forms_ML.value = res;
                    updateWizardData(wizardData)
                }, (error) => {
                    console.error(error);
                });
    }

    const filterContingencyList = () => contingencies?.filter(form => form.docSubType_ML === "payroll_authorization") || []


     //refresh Pre Bind
        const refreshPreBindContingencyData = () => {
            const loadPromise = EndorsementService.refreshPreBindContingencies(endorsementVM.policyNumber.value, authHeader);

            showProgressDialog(platformMessages.getPolicyDetails,  loadPromise);
            loadPromise.then((contingencyData) => {

                if (contingencyData.length > 0) {
                    setcontingencies(contingencyData);
                    setIsFileUploadDone(false);

                } else {

                    if(filterContingencyList()?.length > 0){
                        getNewlyAddedForms()
                    }

                    setcontingencies([]);
                    setIsFileUploadDone(false);

                }
            });
        };

    const nextStep = () => {
        switch (currentStep) {
            case steps.VERBAL_AUTH: {
                initializeDisclaimerStep();
                break;
            }
            case steps.DISCLAIMER : {
                 if(oecRadioSelection === true){
                    setScriptML([]);
                    setCurrentStep(steps.REQUIRED_SIGNATURES);
                    MLNavigationStepsTrackingService.stepChange(null, 'endorsementVerbalAuthorization');
                }
                else{
                    goToQuotePage();
                }

                break;
            }
            case steps.REQUIRED_SIGNATURES: {
                setCurrentStep(steps.AVAILABLE_DOCUMENTS);
                MLNavigationStepsTrackingService.stepChange(null, 'endorsementRequiredSignatures');
                break;
            }
            case steps.AVAILABLE_DOCUMENTS: {
                const bindPromise = EndorsementService.bindEndorsement(endorsementVM.jobID.value, endorsementVM.paymentDetails_ML.value, authHeader);
                showProgressDialog(platformMessages.savingPolicyChange, bindPromise);
                bindPromise.then(({ policyChange, ...rest }) => {
                    MLTagManagerService.sendFormStepPolicyChangeStep();
                    MLNavigationStepsTrackingService.stepChange(null, currentStep.mlStepName);
                    endorsementVM.value = new PolicyChange(policyChange, rest);
                    goNext();
                }, (error) => {
                    if(error.gwInfo && error.gwInfo.params) {
                        error.gwInfo.params[0] = endorsementVM.value;
                    }

                    const payrollAuthErrMessage = "pending payroll authorization contingency";

                    if(error.baseError.indexOf(translator(payrollAuthErrMessage)) > -1){
                        refreshPreBindContingencyData()
                    } else {
                        // Calling Promise.reject() will force the user to the ContactUs page.
                        Promise.reject(error);
                    }      
                });
                break;
            }
            default: {
                break;
            }
        }
    };
    const isEsignExt = _.get(endorsementVM, 'esignature_ML.esign_Ext.value');
    const isEsignFalseButSignatureSelected = () => {
    const isEsignExt = _.get(endorsementVM, 'esignature_ML.esign_Ext.value', false);
 
    if (isEsignExt === true) {
        return false;
    }
 
    return requiredSignaturesForms.some((form) => {
        return _.get(form, 'signatureType.value.code') === 'esignature';
    });
};
   
    const onNext = () => {
        setDisplayEmailWarning(false);
        setShowErrors(false);
        clearWarningAlerts('esignNotAllowed');

        if(currentStep === steps.DISCLAIMER){
            nextStep();
            return;
         }
        if (
          currentStep === steps.REQUIRED_SIGNATURES &&
          isEsignFalseButSignatureSelected() && endorsementVM?.isModernProduct_Ext?.value && endorsementVM?.baseData?.productCode?.value === 'HOPHomeowners'
        ) {
          addWarningAlert(
            translator(messages.esignWarningMsg),
            "esignNotAllowed",
          );
        }

        if (!isComponentValid || endorsementVM.value.blockNavigation_ML) {
            //DE255327 field level error message's
            setShowErrors(true);
            setTimeout(()=>{
                setErrorTimestamp(errorTimestamp+1);
            }, 100);
            return;
        }

        if (isAgent) {
            PolChangeTrackEvent('Endorsement Step Submitted', false, endorsementVM, authHeader, {
                step: 'PolChange Agent Signature Options Screen',
                action: 'Agent Signature Options Submitted'
            });
        }
        const updateFormPromise = MLFormService.updateForms(endorsementVM.jobID.value, endorsementVM.baseData.forms_ML.value, authHeader);
        showProgressDialog(messages.updatingForms, updateFormPromise);

        updateFormPromise.then(() => {
            if (currentStep !== steps.AVAILABLE_DOCUMENTS) {
                MLTagManagerService.sendFormStepPolicyChangeStep();
            }
            nextStep();
        }, (error) => {
            console.error(error);
        });

        updateForms();
    };

    const handleOnBlur = ( rsForm, event ) => {
        let emailAddress1 =  event.target.value;
        if(emailAddress1){
            emailAddress1=emailAddress1.trim();
        }
        let isValidEmailEntred =  /^[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/.test(emailAddress1)
        if(isValidEmailEntred){
            const checkEmailVerified_Ext = AccountService.checkEmailVerified_Ext(
                emailAddress1,
                authHeader
            );
            showProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
            checkEmailVerified_Ext.then((res) => {
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                console.log("Email verification Api Called");
                if(res === true){
                    let email = rsForm.value;
                    if(email !== "" && email !== undefined){
                        email = email.trim();
                        rsForm.value = email;
                    }
                }
                else{
                    let email = rsForm.value;
                    if(email !== "" && email !== undefined){
                        email = email + " ";
                        rsForm.value = email;
                    }
                }
            }).catch((e) => {
                clearBlockAlerts('invalidEmail');
                setDisplayEmailWarning(true);
                hideProgressDialog(platformMessages.loading, checkEmailVerified_Ext, true);
                rsForm.value = emailAddress1;
                console.log(e);
                window.scrollTo(0, 0);
            });
        }
    }




    const overrideProps = {
        warningInlineNotification: {
            visible: displayEmailWarning,
        },
        verbalAuthorizationComponent: {
            visible: currentStep === steps.VERBAL_AUTH,
            verbalAuthForms,
            convertDateToUTC
        },
        requiredSignaturesComponent: {
            visible: currentStep === steps.REQUIRED_SIGNATURES,
            requiredSignaturesForms : (organization?.name?.toLowerCase().includes(mercerOrganization) &&  gafInfo?.gpcCode === mercerGpcCode) ?
            requiredSignaturesForms.filter((rsForm)=> rsForm.publicID.value !== appConfig.payrollAuthorizationPublicId ||
            gafInfo?.payrollAuthorizationFormIndicator === 'Required') :
            requiredSignaturesForms,
            isSignatureOnFileAvailable,
            appSignatureTypeList,
            signatureTypeList,
            shouldShowLanguageOption,
            clearActionFields,
            signatureTypeChange,
            checkIfEmailsAreDuplicated,
            shouldShowSecondEmail,
            shouldShowSecondMobile,
            updateSecondaryInsuredEmail: (value) => {
                endorsementVM.baseData.secondaryNamedInsured_ML.emailAddress1.value = value;
            },
            onValidate,
            handleOnBlur,
            submissionVM: endorsementVM,
            showValidationErrors
        },
        availableDocumentsComponent: {
            visible: currentStep === steps.AVAILABLE_DOCUMENTS,
            availableDocumentsForms,
            isUserRetentionAgent,
            convertDateToUTC,
            viewDocument,
            uwIssues,
            jobID,
            blockingUWIssuesPresentCallback,
            approvedAllBlockingUWIssuesCallback,
            endorsementVM,
            setIsFileUploadDone,
            refreshPreBindContingencyData,
            contingencies
        },
        disclaimersComponent:{
            visible: currentStep === steps.DISCLAIMER && !_.isEmpty(scriptML),
            setRadioSelection: setoecRadioSelection,
            radioSelection: oecRadioSelection,
            scriptML
        },
        nextButton: {
            disabled: isNextButtonDisabled()
        },
        nextDescription: {
            content: getNextDescription()
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onNext,
            onRefreshPreBind: refreshPreBindContingencyData,
        },
        resolveComponentMap: {
            verbalAuthorizationComponent: VerbalAuthorizationComponent,
            requiredSignaturesComponent: RequiredSignaturesComponent,
            availableDocumentsComponent: AvailableDocumentsComponent,
            disclaimerComponent : DisclaimerComponent
        }
    };

    return (
        <WizardPage
            policyChangePageHeaderML={getPageHeader()}
            showPrevious={false}
            showCancel={false}
            showNext={false}
        >
            {currentStep && <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrideProps}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />}
        </WizardPage>
    );
};

AgentSignatureOptionsPage.propTypes = wizardProps;
export default AgentSignatureOptionsPage;
